import type { CurrencyInfo } from "@/lib/types";
import { isCurrencyCode } from "./types";
import type { CurrencyPresentationSettings } from "./types";

// SM-445 — the wire → contract adapter.
//
// `GET /api/currencies` returns the operator's configuration; the SM-443
// contract consumes `CurrencyPresentationSettings`. This is the one
// place the two meet, and it is deliberately lossy in one direction:
// everything about *permissions* on the wire row (`deposit_ceiling`,
// `withdraw_enabled`, …) is dropped here, because presentation must
// never become a second, disagreeing source of policy. What a user may
// do comes from `/api/wallet/capabilities` and nowhere else.
//
// Every field is passed through as-is and left to `resolve.ts` to
// validate: a blank name, a junk colour, a `javascript:` icon src and a
// nonsense decimals value each fall back independently to the frozen
// seed. That is why this function does no checking of its own — adding
// a second validator would give two answers to the same question.

/**
 * Build presentation settings from the currency registry.
 *
 * Unknown codes are skipped rather than passed through: the platform
 * has exactly three currencies, and a fourth arriving on the wire is a
 * corrupted payload, not a feature. Skipping keeps the other three
 * rendering correctly instead of throwing the whole page away.
 */
export function currencySettingsFromRegistry(
  rows: readonly CurrencyInfo[] | undefined,
): CurrencyPresentationSettings {
  const out: CurrencyPresentationSettings = {};
  for (const row of rows ?? []) {
    if (!isCurrencyCode(row.code)) continue;
    out[row.code] = {
      displayName: row.display_name,
      symbol: row.symbol,
      color: row.color,
      decimals: row.display_precision,
      // An operator image wins; without one, resolve.ts keeps the
      // currency's seeded icon (brand for GOLD, monogram for RED and
      // GREEN) rather than drawing an empty <img>.
      icon: row.image_url ? { kind: "image", src: row.image_url, alt: row.display_name } : undefined,
    };
  }
  return out;
}
